/**
 * #1956 — the one helper every roster mutation calls to refresh the grid.
 *
 * The roster page reads by date range (`useTeamRangeRoster`) while the
 * panels and the older surfaces read per window (`useCycleRoster`). A
 * mutation must refresh both, so this invalidates every cached roster read
 * that can show `cycleId`'s dates: that window's per-window reads (lead and
 * member, every account, every Show filter) and every team range-roster
 * read. With no `cycleId` it refreshes every roster read in the cache.
 * Both also refresh the roster statistics (#2111) and the lead's day verdict
 * (#2173), which are computed from the same live rows. Several windows go in
 * ONE call (`cycleId` may be a list), so those shared reads refetch once.
 */
import type { QueryClient } from "@tanstack/react-query";

import { cycleRosterQueryFilter } from "./useCycleRoster";

export function invalidateRosterReads(
  qc: QueryClient,
  cycleId?: string | Iterable<string> | null,
): Promise<void> {
  if (cycleId) return qc.invalidateQueries(cycleRosterQueryFilter(cycleId));
  return qc.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "cycle-roster" ||
      query.queryKey[0] === "team-range-roster" ||
      query.queryKey[0] === "roster-statistics" ||
      query.queryKey[0] === "day-verdicts" ||
      // #2603 — the held re-solve's change list reads the same live rows.
      query.queryKey[0] === "held-resolve-changes" ||
      // #2797 — the Floor row draws the same live rows.
      query.queryKey[0] === "team-floor",
  });
}
